import { useEffect, useState } from 'react';
import { message, Col, Space } from 'antd';
import { DownloadOutlined, HourglassOutlined, TeamOutlined } from '@ant-design/icons';
import { useLocation, useParams } from "react-router-dom";
import { EventCreatedNextSteps } from '../../Components/EventDetails/EventCreatedNextSteps';
import { EventService } from '../../Services/EventService';
import 'react-multi-email/dist/style.css';
import { AuthProvider } from '../../Providers/authProvider';
import { useAuth } from '../../Contexts/AuthContext';
import { GroupService } from '../../Services/GroupService';
import { BookyTitleWithIcon } from '../../Components/Forms/TitleWithIcon';
import { EventDetails } from '../../Components/EventDetails/EventDetails';
import { DocumentService } from '../../Services/ImageService';
import { BlobDto, WebApiServicesDataTransferObjectsReplyServiceGetRepliesResultDto } from '../../Repository/eventbookyapi';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { ReplyTable } from '../../Components/EventDetails/ReplyTable';
import { WaitlistTable } from '../../Components/EventDetails/WaitlistTable';
import { AddMemberReplyModal } from '../../Components/EventDetails/AddMemberReplyModal';
import { isOnlyOnWaitlist } from '../../Helpers/WaitlistHelper';
import { TextService } from '../../Services/TextService';
import LeftMenuEventDetails from './LeftMenuEventDetails';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { EventHeader } from '../../Components/EventDetails/EventHeader';

const MyEventDetails = () => {
  const [event, setEvent] = useState([] as WebApiServicesDataTransferObjectsReplyServiceGetRepliesResultDto);
  const [messageApi, contextHolder] = message.useMessage();
  const [groups, setGroups] = useState([] as any[]);
  const [isModalOpen, setIsModalOpen] = useState(false as boolean);
  const [imageSelect, setImageSelect] = useState("" as string);
  const [images, setImages] = useState<BlobDto[]>([]);
  const { user } = useAuth();
  const GetText = TextService.GetText;

  let params = useParams();
  // Coming from creating the event (via Skapa design): show what to do next above the replies.
  const isCreated = useLocation().search.indexOf("iscreated") !== -1;

  const loadEvent = (id: string) => {
    EventService.GetEventDetails(id).then(result => {
      setEvent(result);
    });
  };

  const saveEventBannerImage = async (uri?: string) => {
    const result = await EventService.UpdateBannerImage({
      webApiServicesDataTransferObjectsEventServiceUpdateEventBannerImageDto: {
        eventId: event.eventId,
        bannerImagePath: imageSelect ? imageSelect.split("bloburl=")[1] : uri,
        isCustomBannerImage: false
      }
    });

    setImageSelect("");
    message.success(GetText("MyEventDetails_Saved"));
    loadEvent(event.eventUniqueId!);
  }

  const downloadExcel = async () => {
    if (user?.subscriptionLevel === SubscriptionLevel.Free) {
      message.warning(GetText("MyEventDetails_SorryOnlyPremium"));
      return;
    };
    const data = await EventService.GenerateExcelFromEvent({ eventId: event.eventId });
    const url = window.URL.createObjectURL(await data.raw.blob());
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `EventReport_${Date.now()}.xlsx`);
    document.body.appendChild(link);
    link.click();
  }


  const deleteEventBannerImage = async () => {
    const result = await EventService.UpdateBannerImage({
      webApiServicesDataTransferObjectsEventServiceUpdateEventBannerImageDto: {
        eventId: event.eventId,
        bannerImagePath: "",
        isCustomBannerImage: false
      }
    });

    setImageSelect("");
    message.success(GetText("MyEventDetails_HasBeenRemoved"));
    loadEvent(event.eventUniqueId!);
  }

  const onClosePoll = async () => {
    loadEvent(event.eventUniqueId!);
  }

  const onOpenPoll = async () => {
    loadEvent(event.eventUniqueId!);
  }

  const onNotificationUpdate = async () => {
    loadEvent(event.eventUniqueId!);
  }

  const onReplyDelete = async () => {
    loadEvent(event.eventUniqueId!);
  }

  const loadImages = async () => {
    DocumentService.GetBannerImages().then(result => {
      setImages(result);
    });
  }

  useEffect(() => {

    AuthProvider().ValidateUserLogin();
    if (params.id != null) {
      loadEvent(params.id);

      GroupService.GetGroups().then(result => {
        setGroups(result);
      });

      loadImages();
    }
  }, [params])


  return <LayoutLeftMenu hasTitleTop={true} header={<EventHeader event={event} />} fullpageMain={true} title={GetText("SendMail_SendMailTitle")} menu={<LeftMenuEventDetails selected={"myevents"} membersOnly={event.membersOnly} />}>
    <Col xs={24} md={18} lg={16}>
      {contextHolder}
      {isCreated && event.eventUniqueId ?
        <Col className='event-page-box' style={{ marginBottom: "20px", textAlign: "left" }}>
          <EventCreatedNextSteps event={event} />
        </Col> : null}
      <Col className='bookyBox event-page-box'>
        <EventDetails event={event} onOpenPoll={onOpenPoll} onClosed={onClosePoll}onNotificationUpdate={onNotificationUpdate} />
      </Col>

      <Col className='bookyBox event-page-box' style={{ marginTop: "20px" }}>
        <BookyTitleWithIcon text={GetText("MyEventDetails_Replies")} rightContent={<Space size="large"><AddMemberReplyModal event={event} onAdded={onReplyDelete} /><span style={{ cursor: "pointer" }} onClick={downloadExcel}>{GetText("MyEventDetails_Download")} <DownloadOutlined alt="Download Excel" /></span></Space>}><TeamOutlined /></BookyTitleWithIcon>

        {/* Guests that only are on the waitlist are listed in the waitlist box, not among the replies. */}
        {event?.timeAnswers?.filter(reply => !isOnlyOnWaitlist(reply)).length === 0 ? <i>{GetText("MyEventDetails_NoRepliesFromGuests")}</i> : <ReplyTable onReplyDelete={onReplyDelete} user={user} onClosePoll={onClosePoll} event={event} />}
        {(event?.timeAnswers?.filter(isOnlyOnWaitlist).length ?? 0) > 0 ? <div style={{ textAlign: "left", marginTop: "10px" }}><i>{event.timeAnswers!.filter(isOnlyOnWaitlist).length} {GetText("MyEventDetails_OnWaitlistBelow")}</i></div> : null}
      </Col>

      {(event?.allowWaitlist || event?.times?.some(time => (time.waitlistCount ?? 0) > 0)) ?
        <Col className='bookyBox event-page-box' style={{ marginTop: "20px" }}>
          <BookyTitleWithIcon text={GetText("Waitlist_Title")}><HourglassOutlined /></BookyTitleWithIcon>
          <WaitlistTable event={event} onPromoted={onReplyDelete} onReplyDelete={onReplyDelete} />
        </Col> : null}
    </Col>


  </LayoutLeftMenu>
};

export default MyEventDetails;